import React, { useState } from 'react';
import { FiX } from 'react-icons/fi';
import AssigneePicker from './AssigneePicker';

const RFIDetailModal = ({ isOpen, onClose, rfi, token, onUpdate }) => {
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(rfi || {});
  const [loading, setLoading] = useState(false);

  if (!isOpen || !rfi) return null;

  const handleUpdate = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rfis/${rfi.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subject: formData.subject,
          description: formData.description,
          status: formData.status,
          response: formData.response,
          assignee: formData.assignee
        })
      });
      
      if (response.ok) {
        onUpdate();
        setEditMode(false);
      }
    } catch (error) {
      console.error('Failed to update RFI:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    return dateStr.split('T')[0];
  };

  const renderTextWithLinks = (text) => {
    if (!text) return null;
    
    // Regular expression to detect URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--monday-blue)', textDecoration: 'underline' }}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 className="modal-title">{rfi.rfiId}</h2>
          <button className="modal-close" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Subject</label>
            {editMode ? (
              <input
                type="text"
                className="input"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              />
            ) : (
              <div style={{ fontSize: '16px', fontWeight: 600 }}>{rfi.subject}</div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            {editMode ? (
              <>
                <textarea
                  className="input"
                  rows="4"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
                <p className="form-helper">Add links by pasting URLs directly in the text</p>
              </>
            ) : (
              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {renderTextWithLinks(rfi.description)}
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            {editMode ? (
              <select
                className="input"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="Open">Open</option>
                <option value="In Review">In Review</option>
                <option value="Closed">Closed</option>
              </select>
            ) : (
              <span className={`status-pill status-${rfi.status?.toLowerCase().replace(' ', '-')}`}>
                {rfi.status}
              </span>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Assignee</label>
            {editMode ? (
              <AssigneePicker
                assignees={formData.assignee ? [formData.assignee] : []}
                onChange={(assignees) => setFormData({ ...formData, assignee: assignees[0] || null })}
                token={token}
              />
            ) : (
              <div>{rfi.assignee || 'Not assigned'}</div>
            )}
          </div>
          {rfi.dueDate && (
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <div>{new Date(rfi.dueDate).toLocaleDateString()}</div>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Response</label>
            {editMode ? (
              <textarea
                className="input"
                rows="4"
                value={formData.response || ''}
                onChange={(e) => setFormData({ ...formData, response: e.target.value })}
                placeholder="Add response..."
              />
            ) : (
              <div>{rfi.response || 'No response yet'}</div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          {editMode ? (
            <>
              <button className="btn btn-secondary" onClick={() => setEditMode(false)} disabled={loading}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleUpdate} disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
              <button className="btn btn-primary" onClick={() => setEditMode(true)}>
                Edit
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RFIDetailModal;